import { createContext, useContext, useState, useCallback } from 'react';

const de = {
  // Common
  save: 'Speichern',
  saving: 'Speichern...',
  cancel: 'Abbrechen',
  delete: 'Löschen',
  confirm: 'Bestätigen',
  loading: 'Laden...',
  search: 'Suchen',
  filter: 'Filtern',
  actions: 'Aktionen',
  status: 'Status',
  close: 'Schließen',
  all: 'Alle',
  active: 'Aktiv',
  inactive: 'Inaktiv',
  back: 'Zurück',
  yes: 'Ja',
  no: 'Nein',
  error: 'Fehler',
  success: 'Erfolg',
  copy: 'Kopieren',
  preview: 'Vorschau',
  configure: 'Konfigurieren',
  editConfig: 'Bearbeiten',

  // Login
  loginTitle: 'Anmelden',
  loginSubtitle: 'Geben Sie Ihre Zugangsdaten ein',
  loginTagline: 'Omnichannel-Support mit KI',
  email: 'E-Mail',
  password: 'Passwort',
  signIn: 'Anmelden',
  signOut: 'Abmelden',
  welcomeBack: 'Willkommen zurück',
  invalidCredentials: 'Ungültige Anmeldedaten',
  fillAllFields: 'Bitte füllen Sie alle Felder aus',
  demoCredentials: 'Demo-Zugangsdaten',

  // Roles
  superadmin: 'Super Admin',
  tenant_admin: 'Mandanten-Admin',
  agent: 'Agent',
  viewer: 'Betrachter',

  // Navigation
  controlPlane: 'System-Leitstand',
  tenants: 'Mandanten',
  auditLog: 'Audit-Protokoll',
  dashboard: 'Dashboard',
  inbox: 'Posteingang',
  knowledgeBase: 'Wissensdatenbank',
  settings: 'Einstellungen',
  channels: 'Kanäle',
  team: 'Team',
  aiPolicy: 'KI-Richtlinien',
  branding: 'Markendesign',
  widget: 'Widget',
  tenantDetail: 'Mandanten-Details',
  conversation: 'Konversation',

  // Dashboard
  welcomeBackName: (name) => `Willkommen zurück, ${name}`,
  dashboardSubtitle: 'Hier ist der aktuelle Stand Ihrer Support-Warteschlange',
  open: 'Offen',
  pending: 'Wartend',
  resolved: 'Gelöst',
  teamMembers: 'Teammitglieder',
  needAttention: 'Erfordern Aufmerksamkeit',
  waitingOnCustomer: 'Wartet auf Kunden',
  thisPeriod: 'Diesen Zeitraum',
  activeAgents: 'Aktive Agenten',
  recentConversations: 'Letzte Konversationen',
  openInbox: 'Posteingang öffnen',
  noConversations: 'Keine Konversationen vorhanden',
  indexedSources: 'Indexierte Quellen',
  manageKB: 'Wissensdatenbank verwalten',

  // Knowledge Base
  kbTitle: 'Wissensdatenbank',
  kbSourcesIndexed: (n, t) => `${n}/${t} Quellen indexiert`,
  kbProcessing: (n) => `(${n} werden verarbeitet...)`,
  addSource: 'Quelle hinzufügen',
  totalSources: 'Gesamt',
  indexed: 'Indexiert',
  processing: 'In Bearbeitung',
  totalChunks: 'Chunks gesamt',
  errors: 'Fehler',
  semanticSearch: 'Semantische Suche',
  semanticSearchHint: 'Testen Sie Ihre Wissensdatenbank mit einer Frage',
  searchPlaceholder: 'z.B. Wie setze ich mein Passwort zurück?',
  noSearchResults: 'Keine Ergebnisse gefunden',
  filterSources: 'Quellen filtern...',
  noKBSources: 'Keine Wissensquellen vorhanden',
  addFirstSource: 'Erste Quelle hinzufügen',
  kbEmptyExplain: 'Der KI-Assistent kann auch ohne Wissensdatenbank allgemein chatten und Anliegen aufnehmen. Konkrete Fragen zu Preisen, Leistungen oder Unternehmensdetails beantwortet er nur, wenn passende, gesicherte Wissensquellen vorhanden sind. Fügen Sie eine erste Quelle hinzu, damit die KI fundierte Antworten geben kann.',
  addKBTitle: 'Wissensquelle hinzufügen',
  addKBDesc: 'Inhalte werden in Chunks aufgeteilt, eingebettet und für RAG-Abruf indexiert.',
  sourceType: 'Typ',
  sourceName: 'Name',
  sourceContent: 'Inhalt',
  sourceURL: 'URL',
  sourceAdded: 'Wissensquelle hinzugefügt — Indexierung gestartet',
  sourceDeleted: 'Quelle gelöscht',
  deleteSourceConfirm: 'Diese Wissensquelle und ihre Vektoren löschen?',
  reingestionStarted: 'Neu-Indexierung gestartet',
  syncFailed: 'Synchronisation fehlgeschlagen',
  addAndIngest: 'Hinzufügen & Indexieren',
  adding: 'Wird hinzugefügt...',
  provideNameAndContent: 'Bitte Name und Inhalt/URL angeben',
  faq: 'FAQ',
  document: 'Dokument',
  url: 'URL',
  article: 'Artikel',
  faqHint: 'Häufig gestellte Fragen als Freitext eingeben',
  articleHint: 'Hilfeartikel oder Dokumentation einfügen',
  urlHint: 'Webseiten-URL zur automatischen Indexierung',
  documentHint: 'Dokument-Inhalt direkt einfügen',
  contentPlaceholder: 'Inhalt hier einfügen...',

  // Index Health
  indexHealth: 'Index-Zustand',
  healthy: 'Gesund',
  degraded: 'Beeinträchtigt',
  indexError: 'Fehlerhaft',
  provider: 'Anbieter',
  model: 'Modell',
  dimensions: 'Dimensionen',
  backend: 'Backend',
  reindexAll: 'Alle neu indexieren',
  reindexWarning: (n) => `${n} Quelle(n) wurden mit einem anderen Embedding-Modell indexiert. Neu-Indexierung wird empfohlen.`,
  mixedModelsWarning: (models) => `Gemischte Embedding-Modelle erkannt: ${models}. Dies kann zu inkonsistenten Suchergebnissen führen.`,
  reindexConfirm: 'Alle Quellen neu indexieren? Dies wird alle Wissensbank-Inhalte neu einbetten und speichern.',
  reindexQueued: 'Neu-Indexierung für alle Quellen gestartet',

  // AI Settings
  aiSettingsTitle: 'KI-Einstellungen',
  aiSettingsDesc: 'KI-Anbieter, Abruf und Sicherheitsrichtlinien konfigurieren',
  aiProviderStatus: 'KI-Anbieter-Status',
  aiFeatures: 'KI-Funktionen',
  enableAI: 'KI-Assistent aktivieren',
  enableAIDesc: 'KI-Analyse, Vorschläge und RAG-basierte Antworten im Posteingang anzeigen',
  autoReply: 'Automatische Antwort',
  autoReplyDesc: 'KI antwortet automatisch bei hohem Konfidenz-Wert',
  escalationDetection: 'Eskalationserkennung',
  escalationDetectionDesc: 'Konversationen automatisch zur menschlichen Eskalation markieren',
  responseConfig: 'Antwort-Konfiguration',
  toneOfVoice: 'Tonalität',
  confidenceThreshold: 'Konfidenz-Schwellenwert',
  moreReplies: 'Mehr Antworten',
  onlyCertain: 'Nur bei Sicherheit',
  systemInstructions: 'System-Anweisungen',
  systemInstructionsPlaceholder: 'Sie sind ein hilfreicher Support-Agent für [Firma]. Seien Sie professionell, einfühlsam und präzise...',
  knowledgeRetrieval: 'Wissensabruf (RAG)',
  topKResults: 'Top-K Ergebnisse',
  focused: 'Fokussiert',
  broad: 'Breit',
  minScoreThreshold: 'Min. Score-Schwellenwert',
  includeMore: 'Mehr einbeziehen',
  highRelevance: 'Hohe Relevanz',
  safetyPolicies: 'Sicherheitsrichtlinien',
  forbiddenTopics: 'Verbotene Themen',
  forbiddenTopicsPlaceholder: 'Wettbewerber-Preise, Rechtsberatung, medizinische Beratung',
  autoReplyCategories: 'Auto-Antwort Kategorien',
  autoReplyWarning: 'Automatische Antwort ist aktiviert',
  autoReplyWarningDesc: (threshold) => `Die KI sendet automatisch Antworten bei Konfidenz über ${threshold}. Bitte genau überwachen.`,
  embeddings: 'Embeddings',
  vectors: 'Vektoren',
  chunks: 'Chunks',

  // Professional/tones
  professional: 'Professionell',
  friendly: 'Freundlich',
  formal: 'Formell',
  casual: 'Locker',
  empathetic: 'Einfühlsam',

  // Widget Settings
  widgetTitle: 'Website-Widget',
  widgetDesc: 'KI-gestützten Support-Chat auf Ihrer Website einbetten',
  widgetFeatures: 'Widget-Funktionen',
  enableWidget: 'Widget aktivieren',
  enableWidgetDesc: 'Widget auf Ihrer Website verfügbar machen',
  aiFirstMode: 'KI-Zuerst-Modus',
  aiFirstModeDesc: 'KI beantwortet zuerst, bevor an Menschen weitergeleitet wird',
  requireEmail: 'E-Mail erforderlich',
  requireEmailDesc: 'Besucher müssen E-Mail angeben, bevor der Chat startet',
  configuration: 'Konfiguration',
  welcomeMessage: 'Begrüßungsnachricht',
  themeColor: 'Design-Farbe',
  position: 'Position',
  bottomRight: 'Unten rechts',
  bottomLeft: 'Unten links',
  offlineMessage: 'Offline-Nachricht',
  embedCode: 'Einbettungscode',
  pasteBeforeBody: 'Vor dem schließenden </body>-Tag einfügen.',
  widgetPreview: 'Widget-Vorschau',
  openFullPreview: 'Vollständige Vorschau öffnen',
  howWidgetWorks: 'So funktioniert das Widget',
  widgetSteps: [
    'Besucher öffnet das Widget auf Ihrer Website',
    'KI durchsucht die Wissensdatenbank nach relevanten Antworten',
    'Bei hoher Konfidenz antwortet die KI automatisch',
    'Besucher kann jederzeit an einen Agenten eskalieren',
    'Vollständige Konversation erscheint in Ihrem Posteingang',
  ],
  copied: 'Kopiert!',
  widgetSaved: 'Widget-Einstellungen gespeichert',

  // Channels
  channelsTitle: 'Kanal-Konfiguration',
  channelsDesc: 'Verbinden Sie Ihre Kommunikationskanäle. Zugangsdaten werden sicher pro Mandant gespeichert.',
  channelArchitecture: 'Multi-Kanal-Architektur',
  channelArchitectureDesc: 'Jeder Kanal speist Konversationen in den zentralen Posteingang. Konfigurieren Sie zuerst die Integration, dann fügen Sie hier Zugangsdaten hinzu.',
  notConfigured: 'Nicht konfiguriert',
  validationFailed: 'Validierung fehlgeschlagen',
  ready: 'Bereit',
  connected: 'Verbunden',
  incomplete: 'Unvollständig',
  credentialsStored: 'Zugangsdaten werden verschlüsselt pro Mandant gespeichert.',
  channelUpdated: (name) => `${name}-Kanal aktualisiert`,
  noExternalCredentials: 'Einbettbares Chat-Widget für Ihre Website. Keine externen Zugangsdaten erforderlich.',
  connectAccount: (label) => `Verbinden Sie Ihr ${label} Business-Konto über API-Zugangsdaten.`,
  whatsappPhoneHint: 'Format: Nur Ziffern, mit Ländervorwahl (z.B. 491701234567)',
  disconnectChannel: 'Verbindung trennen',
  disconnectConfirm: (name) => `Verbindung zu ${name} trennen? Die Zugangsdaten werden entfernt.`,

  // Team
  teamTitle: 'Team-Verwaltung',
  activeMembers: (n) => `${n} aktive Mitglieder`,
  inviteMember: 'Mitglied einladen',
  noTeamMembers: 'Noch keine Teammitglieder',
  inviteTitle: 'Teammitglied einladen',
  fullName: 'Vollständiger Name',
  role: 'Rolle',
  initialPassword: 'Initiales Passwort',
  sendInvite: 'Einladung senden',
  inviting: 'Wird eingeladen...',
  memberInvited: (name) => `${name} wurde erfolgreich eingeladen`,
  deactivateConfirm: (name) => `${name} deaktivieren?`,
  memberDeactivated: 'Mitglied deaktiviert',
  roleUpdated: 'Rolle aktualisiert',
  admin: 'Admin',
  member: 'Mitglied',
  memberCol: 'Mitglied',
  emailCol: 'E-Mail',
  roleCol: 'Rolle',
  statusCol: 'Status',
  actionsCol: 'Aktionen',
  presenceOnline: 'Online',
  presenceOffline: 'Offline',
  lastSeen: 'Zuletzt aktiv',
  lastSeenNever: 'Noch nie aktiv',

  // Branding
  brandingTitle: 'Markendesign',
  brandingDesc: 'Passen Sie an, wie Ihr Support-Portal für Kunden erscheint',
  brandIdentity: 'Markenidentität',
  companyName: 'Firmenname',
  supportEmail: 'Support-E-Mail',
  websiteURL: 'Website-URL',
  primaryColor: 'Primärfarbe',
  logoURL: 'Logo-URL',
  logoHint: 'Empfohlen: 200x50px SVG oder PNG',
  widgetWelcomeMsg: 'Widget-Begrüßungsnachricht',
  brandingSaved: 'Markendesign gespeichert',

  // SuperAdmin
  saStats: 'Systemübersicht',
  totalTenants: 'Mandanten gesamt',
  totalUsers: 'Benutzer gesamt',
  activeTenants: 'Aktive Mandanten',
  totalConversations: 'Konversationen gesamt',

  // Widget (public-facing)
  widgetAISupport: 'KI-gestützter Support',
  widgetConnectedToSupport: 'Mit Support verbunden',
  widgetTypeMessage: 'Nachricht eingeben...',
  widgetTalkToHuman: 'Mit einem Agenten sprechen',
  widgetPoweredBy: 'Powered by FyreSupport',
  widgetEmailRequired: 'Bitte geben Sie Ihre E-Mail-Adresse ein, um den Chat zu starten.',
  widgetEmailPlaceholder: 'Ihre E-Mail-Adresse',
  widgetStartChat: 'Chat starten',
  widgetInvalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
  widgetMessageTooLong: 'Nachricht ist zu lang (max. 4000 Zeichen)',
  widgetSomethingWrong: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
  widgetUnavailable: 'Widget nicht verfügbar',

  // Notifications
  notificationsBtnHint: 'Benachrichtigungen (in Kürze verfügbar)',

  // Pilot Feedback
  feedback: 'Feedback',
  pilotFeedback: 'Pilot-Feedback',
  globalFeedback: 'Globales Feedback',
  globalFeedbackSubtitle: 'Widget-Feedback über alle Mandanten hinweg',
  feedbackTenant: 'Mandant',
  feedbackSubtitle: 'KI-Antwortqualität aus dem Widget',
  feedbackEmpty: 'Noch kein Feedback vorhanden.',
  feedbackHelpful: 'Hilfreich',
  feedbackNotHelpful: 'Nicht hilfreich',
  feedbackRating: 'Bewertung',
  feedbackComment: 'Kommentar',
  feedbackDate: 'Datum',
  feedbackConversation: 'Konversation',
  feedbackMessage: 'Nachricht',
  feedbackTotal: 'Gesamt',
  feedbackUp: 'Daumen hoch',
  feedbackDown: 'Daumen runter',
};

const en = {
  save: 'Save',
  saving: 'Saving...',
  cancel: 'Cancel',
  delete: 'Delete',
  confirm: 'Confirm',
  loading: 'Loading...',
  search: 'Search',
  filter: 'Filter',
  actions: 'Actions',
  status: 'Status',
  close: 'Close',
  all: 'All',
  active: 'Active',
  inactive: 'Inactive',
  back: 'Back',
  yes: 'Yes',
  no: 'No',
  error: 'Error',
  success: 'Success',
  copy: 'Copy',
  preview: 'Preview',
  configure: 'Configure',
  editConfig: 'Edit Configuration',

  loginTitle: 'Sign in',
  loginSubtitle: 'Enter your credentials to access your workspace',
  loginTagline: 'Omnichannel support, powered by AI',
  email: 'Email',
  password: 'Password',
  signIn: 'Sign In',
  signOut: 'Sign out',
  welcomeBack: 'Welcome back',
  invalidCredentials: 'Invalid credentials',
  fillAllFields: 'Please fill in all fields',
  demoCredentials: 'Demo Credentials',

  superadmin: 'Super Admin',
  tenant_admin: 'Mandanten-Admin',
  agent: 'Agent',
  viewer: 'Viewer',

  controlPlane: 'Control Plane',
  tenants: 'Tenants',
  auditLog: 'Audit Log',
  dashboard: 'Dashboard',
  inbox: 'Inbox',
  knowledgeBase: 'Knowledge Base',
  settings: 'Settings',
  channels: 'Channels',
  team: 'Team',
  aiPolicy: 'AI Policy',
  branding: 'Branding',
  widget: 'Widget',
  tenantDetail: 'Tenant Detail',
  conversation: 'Conversation',

  welcomeBackName: (name) => `Welcome back, ${name}`,
  dashboardSubtitle: "Here's what's happening with your support queue",
  open: 'Open',
  pending: 'Pending',
  resolved: 'Resolved',
  teamMembers: 'Team Members',
  needAttention: 'Need attention',
  waitingOnCustomer: 'Waiting on customer',
  thisPeriod: 'This period',
  activeAgents: 'Active agents',
  recentConversations: 'Recent Conversations',
  openInbox: 'Open Inbox',
  noConversations: 'No conversations yet',
  indexedSources: 'Indexed sources',
  manageKB: 'Manage Knowledge Base',

  kbTitle: 'Knowledge Base',
  kbSourcesIndexed: (n, t) => `${n}/${t} sources indexed`,
  kbProcessing: (n) => `(${n} processing...)`,
  addSource: 'Add Source',
  totalSources: 'Total Sources',
  indexed: 'Indexed',
  processing: 'Processing',
  totalChunks: 'Total Chunks',
  errors: 'Errors',
  semanticSearch: 'Semantic Search',
  semanticSearchHint: 'Test your knowledge base with a natural language query',
  searchPlaceholder: 'e.g. How do I reset my password?',
  noSearchResults: 'No results found',
  filterSources: 'Filter sources...',
  noKBSources: 'No knowledge sources',
  addFirstSource: 'Add your first source',
  kbEmptyExplain: 'The AI assistant can chat generally and take requests even without a knowledge base. It only answers concrete questions about pricing, services, or company details when reliable knowledge sources exist. Add a first source so the AI can give grounded answers.',
  addKBTitle: 'Add Knowledge Source',
  addKBDesc: 'Content will be chunked, embedded, and indexed for RAG retrieval.',
  sourceType: 'Type',
  sourceName: 'Name',
  sourceContent: 'Content',
  sourceURL: 'URL',
  sourceAdded: 'Knowledge source added — ingestion started',
  sourceDeleted: 'Source deleted',
  deleteSourceConfirm: 'Delete this knowledge source and its vectors?',
  reingestionStarted: 'Re-ingestion started',
  syncFailed: 'Sync failed',
  addAndIngest: 'Add & Ingest',
  adding: 'Adding...',
  provideNameAndContent: 'Provide name and content/URL',
  faq: 'FAQ',
  document: 'Document',
  url: 'URL',
  article: 'Article',
  faqHint: 'Enter frequently asked questions as free text',
  articleHint: 'Paste help article or documentation',
  urlHint: 'Web page URL for automatic indexing',
  documentHint: 'Paste document content directly',
  contentPlaceholder: 'Paste your content here...',

  indexHealth: 'Index Health',
  healthy: 'Healthy',
  degraded: 'Degraded',
  indexError: 'Error',
  provider: 'Provider',
  model: 'Model',
  dimensions: 'Dimensions',
  backend: 'Backend',
  reindexAll: 'Reindex All',
  reindexWarning: (n) => `${n} source(s) were indexed with a different embedding model. Reindex to ensure consistent retrieval quality.`,
  mixedModelsWarning: (models) => `Mixed embedding models detected: ${models}. This may cause inconsistent search results.`,
  reindexConfirm: 'Reindex all sources? This will re-embed and re-store all knowledge base content.',
  reindexQueued: 'Reindex started for all sources',

  aiSettingsTitle: 'AI Settings',
  aiSettingsDesc: 'Configure AI provider, retrieval, and safety policies',
  aiProviderStatus: 'AI Provider Status',
  aiFeatures: 'AI Features',
  enableAI: 'Enable AI Assistant',
  enableAIDesc: 'Show AI analysis, suggestions, and RAG-grounded replies in the inbox',
  autoReply: 'Auto Reply',
  autoReplyDesc: 'Allow AI to automatically reply when confidence exceeds threshold',
  escalationDetection: 'Escalation Detection',
  escalationDetectionDesc: 'Automatically flag conversations for human escalation',
  responseConfig: 'Response Configuration',
  toneOfVoice: 'Tone of Voice',
  confidenceThreshold: 'Confidence Threshold',
  moreReplies: 'More replies',
  onlyCertain: 'Only certain',
  systemInstructions: 'System Instructions',
  systemInstructionsPlaceholder: 'You are a helpful support agent for [Company]. Always be professional, empathetic, and concise...',
  knowledgeRetrieval: 'Knowledge Retrieval (RAG)',
  topKResults: 'Top-K Results',
  focused: 'Focused',
  broad: 'Broad',
  minScoreThreshold: 'Min Score Threshold',
  includeMore: 'Include more',
  highRelevance: 'High relevance',
  safetyPolicies: 'Safety Policies',
  forbiddenTopics: 'Forbidden Topics',
  forbiddenTopicsPlaceholder: 'competitor pricing, legal advice, medical advice',
  autoReplyCategories: 'Auto-Reply Categories',
  autoReplyWarning: 'Auto Reply is enabled',
  autoReplyWarningDesc: (threshold) => `The AI will automatically send replies when confidence exceeds ${threshold}. Monitor closely to ensure quality.`,
  embeddings: 'Embeddings',
  vectors: 'Vectors',
  chunks: 'Chunks',

  professional: 'Professional',
  friendly: 'Friendly',
  formal: 'Formal',
  casual: 'Casual',
  empathetic: 'Empathetic',

  widgetTitle: 'Website Widget',
  widgetDesc: 'Embed an AI-powered support chat on your website',
  widgetFeatures: 'Widget Features',
  enableWidget: 'Enable Widget',
  enableWidgetDesc: 'Make the widget available on your website',
  aiFirstMode: 'AI-First Mode',
  aiFirstModeDesc: 'AI handles first response before routing to humans',
  requireEmail: 'Require Email',
  requireEmailDesc: 'Ask visitors for email before starting chat',
  configuration: 'Configuration',
  welcomeMessage: 'Welcome Message',
  themeColor: 'Theme Color',
  position: 'Position',
  bottomRight: 'Bottom Right',
  bottomLeft: 'Bottom Left',
  offlineMessage: 'Offline Message',
  embedCode: 'Embed Code',
  pasteBeforeBody: 'Paste before the closing </body> tag.',
  widgetPreview: 'Widget Preview',
  openFullPreview: 'Open Full Preview',
  howWidgetWorks: 'How the Widget Works',
  widgetSteps: [
    'Visitor opens widget on your website',
    'AI checks Knowledge Base for relevant answers',
    'If confident, AI replies automatically',
    'Visitor can escalate to a team member anytime',
    'Full conversation appears in your Inbox',
  ],
  copied: 'Copied!',
  widgetSaved: 'Widget settings saved',

  channelsTitle: 'Channel Configuration',
  channelsDesc: 'Connect your communication channels. Credentials are stored securely per tenant.',
  channelArchitecture: 'Multi-channel architecture',
  channelArchitectureDesc: 'Each channel feeds conversations into the central inbox. Configure the integration first, then add channel credentials here.',
  notConfigured: 'Not configured',
  validationFailed: 'Validation failed',
  ready: 'Ready',
  connected: 'Connected',
  incomplete: 'Incomplete',
  credentialsStored: 'Credentials are stored encrypted per tenant.',
  channelUpdated: (name) => `${name} channel updated`,
  noExternalCredentials: 'Embeddable chat widget for your website. No external credentials needed.',
  connectAccount: (label) => `Connect your ${label} business account via API credentials.`,
  whatsappPhoneHint: 'Format: digits only, with country code (e.g. 491701234567)',
  disconnectChannel: 'Disconnect',
  disconnectConfirm: (name) => `Disconnect ${name}? Credentials will be removed.`,

  teamTitle: 'Team Management',
  activeMembers: (n) => `${n} active members`,
  inviteMember: 'Invite Member',
  noTeamMembers: 'No team members yet',
  inviteTitle: 'Invite Team Member',
  fullName: 'Full Name',
  role: 'Role',
  initialPassword: 'Initial Password',
  sendInvite: 'Send Invite',
  inviting: 'Inviting...',
  memberInvited: (name) => `${name} invited successfully`,
  deactivateConfirm: (name) => `Deactivate ${name}?`,
  memberDeactivated: 'Member deactivated',
  roleUpdated: 'Role updated',
  admin: 'Admin',
  member: 'Member',
  memberCol: 'Member',
  emailCol: 'Email',
  roleCol: 'Role',
  statusCol: 'Status',
  actionsCol: 'Actions',
  presenceOnline: 'Online',
  presenceOffline: 'Offline',
  lastSeen: 'Last seen',
  lastSeenNever: 'Never seen',

  brandingTitle: 'Branding',
  brandingDesc: 'Customize how your support portal appears to customers',
  brandIdentity: 'Brand Identity',
  companyName: 'Company Name',
  supportEmail: 'Support Email',
  websiteURL: 'Website URL',
  primaryColor: 'Primary Color',
  logoURL: 'Logo URL',
  logoHint: 'Recommended: 200x50px SVG or PNG',
  widgetWelcomeMsg: 'Widget Welcome Message',
  brandingSaved: 'Branding saved',

  saStats: 'System Overview',
  totalTenants: 'Total Tenants',
  totalUsers: 'Total Users',
  activeTenants: 'Active Tenants',
  totalConversations: 'Total Conversations',

  widgetAISupport: 'AI-powered support',
  widgetConnectedToSupport: 'Connected to support',
  widgetTypeMessage: 'Type your message...',
  widgetTalkToHuman: 'Talk to a team member',
  widgetPoweredBy: 'Powered by FyreSupport',
  widgetEmailRequired: 'Please enter your email address to start the chat.',
  widgetEmailPlaceholder: 'Your email address',
  widgetStartChat: 'Start Chat',
  widgetInvalidEmail: 'Please enter a valid email address',
  widgetMessageTooLong: 'Message is too long (max 4000 characters)',
  widgetSomethingWrong: 'Something went wrong. Please try again.',
  widgetUnavailable: 'Widget unavailable',

  notificationsBtnHint: 'Notifications (coming soon)',

  feedback: 'Feedback',
  pilotFeedback: 'Pilot Feedback',
  globalFeedback: 'Global Feedback',
  globalFeedbackSubtitle: 'Widget feedback across all tenants',
  feedbackTenant: 'Tenant',
  feedbackSubtitle: 'AI reply quality from the widget',
  feedbackEmpty: 'No feedback yet.',
  feedbackHelpful: 'Helpful',
  feedbackNotHelpful: 'Not helpful',
  feedbackRating: 'Rating',
  feedbackComment: 'Comment',
  feedbackDate: 'Date',
  feedbackConversation: 'Conversation',
  feedbackMessage: 'Message',
  feedbackTotal: 'Total',
  feedbackUp: 'Thumbs up',
  feedbackDown: 'Thumbs down',
};

const translations = { de, en };

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState('de');

  const setLanguage = useCallback(() => {
    localStorage.setItem('fs_lang', 'de');
    setLang('de');
  }, []);

  const t = translations[lang] || translations.de;

  return (
    <I18nContext.Provider value={{ t, lang, setLanguage }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};
